import { useEffect, useState } from "react";
import {
  Search,
  Phone,
  Mail,
  Send,
  Loader,
} from "lucide-react";
import {
  getPaymentList,
  sendEmail,
} from "@/services/masters";
import GenericTable from "../ui/GenericTable";
import toast from "react-hot-toast";
import Pagination from "../common/Pagination";

interface Child {
  Child_Id: number;
  Line_item_Id: string;
  Child_Name: string;
  Subscription_Code: string;
  Subscription_Status: string;
  Phone_Number: string;
  Email: string;
  Subscription_Start_Date: string | null;
  Subscription_End_Date: string | null;
  Amount: number;
  Status: string;
  Child_Status: string;
  Renewal_Count: number;
  Payment_Status: string;
  Payment_Id: string | null;
  Order_Id: string | null;
  Created_At: string | null;
}

interface ParentPayment {
  Username: string;
  Phone_Number: string;
  Email: string;
  Children: Child[];
}

interface PaymentRow extends Child {
  id: string;
  Username: string;
  Parent_Phone: string;
  Parent_Email: string;
}

interface PaymentResponse {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: ParentPayment[];
}

const ITEMS_PER_PAGE = 20;

const statusStyles: Record<string, string> = {
  Active: "bg-mlb-green/10 text-mlb-green",
  "In-Active": "bg-red-100 text-red-600",
  Paid: "bg-mlb-green/10 text-mlb-green",
  Manual: "bg-mlb-gold/10 text-mlb-gold",
  Razorpay: "bg-blue-100 text-blue-600",
};

function formatDate(date: string | null) {
  if (!date) return "-";

  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatAmount(amount: number) {
  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
  })}`;
}

function StatusBadge({ value }: { value: string }) {
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${
        statusStyles[value] ??
        "bg-gray-100 text-gray-600"
      }`}
    >
      {value}
    </span>
  );
}

export default function PaymentList() {
  const [search, setSearch] = useState("");

  const [data, setData] = useState<ParentPayment[]>([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(
    null
  );

  const [sendingMail, setSendingMail] =
    useState<number | null>(null);

  const [currentPage, setCurrentPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const [totalCount, setTotalCount] = useState(0);

  const [debouncedSearch, setDebouncedSearch] =
    useState("");

  async function getPaymentListData(
    page = currentPage,
    searchValue = debouncedSearch
  ) {
    try {
      setLoading(true);
      setError(null);

      const res: any = await getPaymentList(
        undefined,
        page,
        searchValue
      );

      const json: PaymentResponse =
        res?.data ?? res;

      setData(json?.results ?? []);
      setTotalPages(json?.total_pages ?? 1);
      setTotalCount(json?.count ?? 0);
    } catch (e: any) {
      setError(
        e?.message || "Something went wrong"
      );

      setData([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }

  async function handleSendMail(
    Username: string,
    Child_Id: number
  ) {
    setSendingMail(Child_Id);

    try {
      const response = await sendEmail(
        Username,
        String(Child_Id)
      );

      if (response) {
        toast.success("Mail Sent Successfully");
      }
    } catch (error) {
      console.error(
        "Failed to send email:",
        error
      );

      toast.error("Failed to send mail");
    } finally {
      setSendingMail(null);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getPaymentListData(
      currentPage,
      debouncedSearch
    );
  }, [currentPage, debouncedSearch]);

  const rows: PaymentRow[] = data.flatMap(
    (parent) =>
      (parent.Children ?? []).map((child) => ({
        ...child,
        id: `${child.Line_item_Id}-${child.Child_Id}`,
        Username: parent.Username,
        Parent_Phone: parent.Phone_Number,
        Parent_Email: parent.Email,
      }))
  );

  const columns = [
    {
      key: "Username",
      label: "Username",
      render: (_: any, row: PaymentRow) => (
        <div>
          <div className="font-medium text-gray-900">
            {row.Username}
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            <Phone className="w-3 h-3" />
            {row.Parent_Phone}
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Mail className="w-3 h-3" />
            {row.Parent_Email}
          </div>
        </div>
      ),
    },
    {
      key: "Child_Name",
      label: "Child",
    },
    {
      key: "Subscription_Code",
      label: "Subscription Code",
    },
    {
      key: "Amount",
      label: "Amount",
      render: (value: number) => (
        <span className="font-medium text-gray-900">
          {formatAmount(value)}
        </span>
      ),
    },
    {
      key: "Payment_Status",
      label: "Payment Status",
      render: (value: string) => (
        <StatusBadge value={value} />
      ),
    },
    {
      key: "Subscription_Status",
      label: "Status",
      render: (value: string) => (
        <StatusBadge value={value} />
      ),
    },
    {
      key: "Subscription_Start_Date",
      label: "Start Date",
      render: (value: string | null) =>
        formatDate(value),
    },
    {
      key: "Subscription_End_Date",
      label: "End Date",
      render: (value: string | null) =>
        formatDate(value),
    },
    {
      key: "Created_At",
      label: "Created At",
      render: (value: string | null) =>
        formatDate(value),
    },
    {
      key: "sendMail",
      label: "Send Mail",
      render: (_: any, row: PaymentRow) => (
        <button
          type="button"
          onClick={() =>
            handleSendMail(
              row.Username,
              row.Child_Id
            )
          }
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-mlb-green hover:bg-mlb-green/10 transition-colors"
        >
          {sendingMail === row.Child_Id ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col gap-3 mb-5 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:w-72 md:ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

          <input
            type="text"
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
            placeholder="Search by username, child, or code"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-mlb-green/30"
          />
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 mb-4 text-sm text-red-500">
          {error}
        </div>
      )}
            {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
          />x
        </div>
      )}

      <GenericTable
        title="Payments"
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage="No payment records found."
      />
    </div>
  );
}
